import {
  Popover as MuiPopover,
  PopoverProps as MuiPopoverProps,
  SxProps,
  Theme,
} from '@mui/material';
import { Box } from '../../Layout/Box';
import { CloseButton } from '../../Buttons';
import { forwardRef, type ReactNode } from 'react';

export interface PopoverProps extends Omit<MuiPopoverProps, 'open' | 'onClose'> {
  /**
   * Whether the popover is open
   */
  open: boolean;

  /**
   * Callback fired when the popover should close
   */
  onClose: () => void;

  /**
   * The element that triggers the popover
   */
  anchorEl: HTMLElement | null;

  /**
   * Content to display inside the popover
   */
  children?: ReactNode;

  /**
   * Whether to show close button in the top right
   * @default true
   */
  showCloseButton?: boolean;

  /**
   * Custom close button icon
   */
  closeIcon?: ReactNode;

  /**
   * Custom styles for the popover paper
   */
  paperSx?: SxProps<Theme>;

  /**
   * Custom styles for the content container
   */
  contentSx?: SxProps<Theme>;
}

/**
 * Popover Component
 *
 * A reusable popover component that wraps MUI Popover with a standardized close button.
 * The close button appears in the top right corner by default.
 * Positioning is controlled via anchorOrigin and transformOrigin (anchor positioning).
 *
 * @example
 * ```tsx
 * const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
 *
 * <Button onClick={(e) => setAnchorEl(e.currentTarget)}>Open</Button>
 * <Popover
 *   open={Boolean(anchorEl)}
 *   anchorEl={anchorEl}
 *   onClose={() => setAnchorEl(null)}
 * >
 *   <Typography>Popover content</Typography>
 * </Popover>
 * ```
 */
const defaultAnchorOrigin = { vertical: 'bottom' as const, horizontal: 'left' as const };
const defaultTransformOrigin = { vertical: 'top' as const, horizontal: 'left' as const };

export const Popover = forwardRef<HTMLDivElement, PopoverProps>(
  (
    {
      open,
      onClose,
      anchorEl,
      children,
      showCloseButton = true,
      closeIcon,
      paperSx,
      contentSx,
      anchorOrigin,
      transformOrigin,
      ...other
    },
    ref
  ) => {
    const mergedAnchorOrigin = { ...defaultAnchorOrigin, ...anchorOrigin };
    const mergedTransformOrigin = { ...defaultTransformOrigin, ...transformOrigin };

    const handlePopoverClose = (
      _event: Record<string, never>,
      reason: 'backdropClick' | 'escapeKeyDown'
    ) => {
      if (reason === 'backdropClick') {
        return;
      }
      onClose();
    };

    return (
      <MuiPopover
        ref={ref}
        open={open}
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        anchorOrigin={mergedAnchorOrigin}
        transformOrigin={mergedTransformOrigin}
        PaperProps={{
          sx: [
            (theme: Theme) => ({
              mt: 1,
              borderRadius: theme.shape.borderRadius,
              boxShadow: theme.shadows[8],
            }),
            ...(Array.isArray(paperSx) ? paperSx : paperSx ? [paperSx] : []),
          ],
        }}
        {...other}
      >
        <Box
          sx={[
            (theme: Theme) => ({
              position: 'relative',
              padding: theme.spacing(2),
              minWidth: 300,
            }),
            ...(Array.isArray(contentSx) ? contentSx : contentSx ? [contentSx] : []),
          ]}
        >
          {showCloseButton && (
            <CloseButton
              icon={closeIcon}
              onClick={onClose}
              aria-label="close"
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                zIndex: 1,
                backgroundColor: 'common.midGray',
                color: 'common.white',
              }}
            />
          )}
          {children}
        </Box>
      </MuiPopover>
    );
  }
);

Popover.displayName = 'Popover';
